import { ApolloError } from '@apollo/client';

import type { GraphQLError } from 'graphql';

import {
  BadRequestError,
  InternalError,
  NotAuthorizedError,
} from 'activate-errors';

import type { FailureOutcome, OperationFailure } from '../model/operations';

// The server rejected the write before changing anything. Every other code
// (NF01, SVC01, AE01 and any unrecognised one) may follow a write that landed,
// so it is an unknown outcome.
const DEFINITE_REJECTION_CODES: unknown[] = [
  BadRequestError.errorName,
  NotAuthorizedError.errorName,
  InternalError.errorName,
];

export function readExceptionCode(graphQLError: GraphQLError): unknown {
  const exception = graphQLError.extensions?.exception;

  return typeof exception === 'object' &&
    exception !== null &&
    'code' in exception
    ? exception.code
    : undefined;
}

function isDefiniteRejection(apolloError: ApolloError): boolean {
  const { graphQLErrors, networkError } = apolloError;

  if (networkError || graphQLErrors.length === 0) {
    return false;
  }

  return graphQLErrors.every(graphQLError => {
    const code = readExceptionCode(graphQLError);

    return DEFINITE_REJECTION_CODES.includes(code);
  });
}

function resolveOutcome(error: unknown): FailureOutcome {
  if (!(error instanceof Error)) {
    return 'unknown';
  }

  const source = error instanceof ApolloError ? error : error.cause;

  // Hook wrappers attach no cause when they fail before sending or when the
  // server answered success: false
  if (source === undefined) {
    return 'definite';
  }

  return source instanceof ApolloError && isDefiniteRejection(source)
    ? 'definite'
    : 'unknown';
}

export function classifyOperationError(error: unknown): OperationFailure {
  const message = error instanceof Error ? error.message : String(error);
  const outcome = resolveOutcome(error);

  return { outcome, message, cause: error };
}
